<template>
  <div>
    <RemoveCardDialog
      :close="closeRemoveCardDialog"
      :input="removeCard.input"
      :token="removeCard.token"
    />
    <v-dialog
      max-width="700"
      :fullscreen="styles.fullscreen"
      :value="dialog"
      :persistent="true"
    >
      <v-card class="px-3 py-10">
        <CloseDialogButton
          :close="() => $store.commit('orders/CLOSE_CREDIT_CARD_DIALOG')"
        />
        <v-row no-gutters>
          <v-col class="text-center">
            <div class="font-size-lg font-weight-semibold">Payment Method</div>
          </v-col>
        </v-row>
        <v-row class="mb-6" no-gutters>
          <v-col class="text-center">
            <v-icon class="mdi-credit-card-settings-outline primary--text"
              >mdi-credit-card-settings-outline
            </v-icon>
          </v-col>
        </v-row>

        <v-row
          v-for="card in payNow.cards"
          :key="card.id"
          align="center"
          no-gutters
        >
          <v-col cols="1" class="text-center">
            <v-icon
              v-if="payNow.selectedCard.id === card.id"
              color="success"
              :small="styles.smallCheck"
              >mdi-check-bold
            </v-icon>
          </v-col>
          <v-col cols="9">
            <v-list>
              <v-list-item
                :disabled="card.isExpired"
                :key="card.id"
                :value="card"
                style="background-color: white"
                @click.stop.prevent="handleCardClick(card)"
              >
                <v-row align="center" no-gutters>
                  <v-col cols="1">
                    <font-awesome-icon
                      :icon="['fab', getFontAwesomeIconName(card.card.brand)]"
                      :size="styles.cardIconSize"
                    />
                  </v-col>
                  <v-col
                    cols="7"
                    class="text-center"
                    :class="styles.xsCardNumberFontSize"
                    >{{ card.card.last4 }}
                  </v-col>
                  <v-col
                    cols="4"
                    class="font-size-xs text-center"
                    :class="
                      card.isExpired
                        ? 'color-fire-engine-red'
                        : 'color-suva-grey'
                    "
                    >{{
                      formatExpirationDate(
                        {
                          month: card.card.exp_month,
                          year: card.card.exp_year,
                        },
                        card.isExpired,
                      )
                    }}
                  </v-col>
                </v-row>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="2" class="text-center">
            <v-btn
              class="font-weight-semibold text-capitalize"
              @click="handleRemoveCardClick(card.id)"
              :x-small="styles.smallRemoveButton"
              color="error"
              text
              >remove
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="mb-6" no-gutters>
          <v-col cols="1"></v-col>
          <v-col cols="9">
            <v-list>
              <v-list-item @click.stop.prevent="handleAddNewCardClick()">
                <v-icon class="mr-6" color="primary">mdi-plus</v-icon>
                <v-list-item-title class="primary--text"
                  >Add New Card
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="2"></v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="text-center">
            <v-btn
              color="success"
              height="48"
              width="180"
              rounded
              :disabled="!payNow.selectedCard.id"
              @click.stop="handlePayWithSelectedCardClick"
            >
              <div class="font-size-md text-capitalize e2e-paymentmethod-done">
                Submit
              </div>
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CloseDialogButton from "@/components/UI/CloseDialogButton.vue";
import RemoveCardDialog from "@/components/Order/Dialogs/RemoveCard.vue";

const styles = {
  xs: {
    cardIconSize: "lg",
    fullscreen: true,
    smallCheck: true,
    smallRemoveButton: true,
    xsCardNumberFontSize: "font-size-xs ",
  },
  smAndUp: {
    cardIconSize: "2x",
    fullscreen: false,
    smallCheck: false,
    smallRemoveButton: false,
    xsCardNumberFontSize: "",
  },
};

export default {
  name: "CardsDialog",
  components: {
    CloseDialogButton,
    RemoveCardDialog,
  },
  computed: {
    ...mapState({
      dialog: (state) => state.orders.dialog.cards,
      error: (state) => state.orders.dialog.orderError.error,
      payNow: (state) => state.orders.payNow,
      orderId: (state) => state.orders.order.id,
      serviceType: (state) => state.orders.order.service,
      deliveryName: (state) => state.store.info.delivery_name,
    }),
    styles() {
      return this.$vuetify.breakpoint.xsOnly ? styles.xs : styles.smAndUp;
    },
  },
  data() {
    return {
      removeCard: {
        input: false,
        token: "",
      },
    };
  },
  methods: {
    closeRemoveCardDialog() {
      this.removeCard.token = "";
      this.removeCard.input = false;
      this.payNow.cards.length &&
        this.$store.commit("orders/OPEN_CREDIT_CARD_DIALOG");
    },
    formatExpirationDate(date, isExpired) {
      const formattedMonth =
        parseInt(date.month) < 10 ? "0" + date.month : date.month;
      const formattedDate = `${formattedMonth}/${date.year}`;
      return isExpired
        ? `Expired ${formattedDate}`
        : `Expires ${formattedDate}`;
    },
    async finishCardPayment() {
      const isBringgOrder =
        this.deliveryName === "Bringg" && this.serviceType === "DELIVERY";

      await this.$router.push(
        isBringgOrder
          ? `/confirmed/${this.orderId}`
          : { path: "home", query: { orderId: this.orderId } },
      );
    },
    getFontAwesomeIconName(sslCardShortDescription) {
      /*
       * https://github.com/FortAwesome/vue-fontawesome
       *
       * valid values for ssl_card_short_description are: AMEX, DISC, MC, and VISA
       * cc-amex, cc-discover, cc-mastercard, cc-visa);
       */
      const sslCardShortDescriptionToFontAwescomeIconNameMap = {
        amex: "cc-amex",
        discover: "cc-discover",
        mastercard: "cc-mastercard",
        visa: "cc-visa",
      };
      return sslCardShortDescriptionToFontAwescomeIconNameMap[
        sslCardShortDescription
      ];
    },
    handleAddNewCardClick() {
      this.$store.commit("orders/CLOSE_CREDIT_CARD_DIALOG");
      this.$store.commit("orders/OPEN_PAYMENT_DIALOG");
    },
    handleCardClick(card) {
      this.$store.commit("orders/UPDATE_SELECTED_CARD", card);
    },
    handleRemoveCardClick(token) {
      this.$store.commit("orders/CLOSE_CREDIT_CARD_DIALOG");
      this.removeCard.input = true;
      this.removeCard.token = token;
    },
    async handlePayWithSelectedCardClick() {
      this.$store.commit("orders/PROCESSING_SAVED_CARD", true);
      this.$store.commit("orders/CLOSE_CREDIT_CARD_DIALOG");
      await this.$store.dispatch("orders/payWithSavedCC");
      this.$store.commit("orders/PROCESSING_SAVED_CARD", false);
      if (!this.error) {
        await this.finishCardPayment();
      }
    },
  },
};
</script>

<style scoped>
.mdi-credit-card-settings-outline {
  font-size: 120px;
}
</style>
